// Printable blank Snapshot for guests and members who aren't using the app yet.

import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Printer } from 'lucide-react';
import { snapshotsAPI } from '../api/client';
import BrandLogo from '../components/BrandLogo';
import { useAuth } from '../context/useAuth';
import { isNativePlatform } from '../native';
import type { SnapshotCategory } from '../types';
import {
  DIRECTION_LABELS,
  OWNER_EXPERIENCE_OPTIONS,
  STATE_LABELS,
  pulsePrompt,
} from '../lib/snapshotFlags';

const BLANK_SNAPSHOT_TYPES = [
  { value: 'member', label: 'Member (business owner)' },
  { value: 'nonprofit', label: 'Nonprofit leader' },
  { value: 'advisor', label: 'Advisor' },
  { value: 'leader', label: 'Leader' },
] as const;

// Letter width minus the 0.5in print margins, in CSS pixels.
const SHEET_WIDTH_PX = 720;

const STATES = [1, 2, 3, 4].map((v) => STATE_LABELS[v]);
const DIRECTIONS = [1, 2, 3].map((v) => DIRECTION_LABELS[v]);

function familyViewLabel(categoryName: string): string {
  return categoryName === 'Parenting & Children' ? "Children's view" : "Spouse's view";
}

function Box() {
  return <span className="blank-box" aria-hidden="true" />;
}

function Line({ label }: { label?: string }) {
  return (
    <div className="blank-line">
      {label && <span className="blank-line-label">{label}</span>}
      <span className="blank-line-rule" />
    </div>
  );
}

export function BlankSnapshotForm({
  snapshotType,
  categories,
  includeDefinitions,
}: {
  snapshotType: string;
  categories: SnapshotCategory[];
  includeDefinitions: boolean;
}) {
  const isMember = snapshotType === 'member';
  return (
    <div className="blank-snapshot">
      <section className="blank-page">
        <div className="report-masthead">
          <BrandLogo variant="color" hideTagline className="report-logo" />
          <div className="report-masthead-text">
            <p className="report-kicker">Member Snapshot™</p>
            <h1>Monthly Snapshot</h1>
          </div>
        </div>

        <div className="blank-fields">
          <Line label="Name" />
          <Line label="Month" />
          <Line label="Forum" />
        </div>

        <h2>{isMember ? 'Entrepreneur Pulse' : 'Pulse'}</h2>
        <div className="blank-pulse">
          <p>{pulsePrompt(snapshotType)}</p>
          <p className="blank-choices">
            {OWNER_EXPERIENCE_OPTIONS.map((o) => (
              <span key={o.value}>
                <Box /> {o.label}
              </span>
            ))}
          </p>
        </div>

        <h2>Foundation</h2>
        <Line label="My Purpose Statement" />

        <h2>Ratings</h2>
        <p className="blank-hint">
          Check one State and one Direction for each area, against what healthy looks like for you:{' '}
          <strong>Breaking</strong> in crisis · <strong>Strained</strong> not where it should be ·{' '}
          <strong>Healthy</strong> meets your definition · <strong>Thriving</strong> well beyond it.{' '}
          <strong>Direction</strong> is where it&rsquo;s heading since last month. Skip any area that doesn&rsquo;t apply.
        </p>
        <table className="blank-table">
          <colgroup>
            <col className="blank-col-area" />
            {STATES.map((label) => (
              <col key={label} className="blank-col-check" />
            ))}
            <col className="blank-col-gap" />
            {DIRECTIONS.map((label) => (
              <col key={label} className="blank-col-check blank-col-dir" />
            ))}
            <col className="blank-col-gap" />
            <col />
          </colgroup>
          <thead>
            <tr className="blank-group-row">
              <th />
              <th colSpan={STATES.length} className="blank-group">State</th>
              <th />
              <th colSpan={DIRECTIONS.length} className="blank-group">Direction</th>
              <th />
              <th className="blank-group blank-group-notes">Notes</th>
            </tr>
            <tr className="blank-label-row">
              <th className="blank-area">Area</th>
              {STATES.map((s) => (
                <th key={s} className="blank-check">{s}</th>
              ))}
              <th />
              {DIRECTIONS.map((d) => (
                <th key={d} className="blank-check">{d}</th>
              ))}
              <th />
              <th />
            </tr>
          </thead>
          {categories.map((c) => {
            const rowCount = 1 + (includeDefinitions ? 1 : 0) + (c.has_spouse_rating ? 1 : 0);
            const checks = (
              <>
                {STATES.map((s) => (
                  <td key={s} className="blank-check"><Box /></td>
                ))}
                <td className="blank-gap" />
                {DIRECTIONS.map((d) => (
                  <td key={d} className="blank-check"><Box /></td>
                ))}
              </>
            );
            return (
              <tbody key={c.id} className="blank-area-group">
                <tr className="blank-rating">
                  <td className="blank-area">
                    <strong>{c.name}</strong>
                    {c.scripture_reference && <em>{c.scripture_reference}</em>}
                  </td>
                  {checks}
                  <td className="blank-gap blank-gap-notes" rowSpan={rowCount} />
                  <td className="blank-note-col" rowSpan={rowCount} />
                </tr>
                {includeDefinitions && (
                  <tr className="blank-define">
                    <td colSpan={2 + STATES.length + DIRECTIONS.length}>
                      <Line label="Healthy looks like" />
                    </td>
                  </tr>
                )}
                {c.has_spouse_rating && (
                  <tr className="blank-family">
                    <td className="blank-area">{familyViewLabel(c.name)} (optional)</td>
                    {checks}
                  </tr>
                )}
              </tbody>
            );
          })}
        </table>

        <div className="blank-columns">
          <div>
            <h2>What&rsquo;s most important right now?</h2>
            <p className="blank-hint">Which area should your group focus on with you this month?</p>
            <Line />
            <Line />
          </div>
          <div>
            <h2>Major Prayer Request</h2>
            <p className="blank-hint">What would you like your group to be praying about this month?</p>
            <Line />
            <Line />
          </div>
        </div>

        <h2>Grow Goals</h2>
        {[1, 2].map((n) => (
          <div key={n} className="blank-goal">
            <Line label={`${n}.`} />
            <span className="blank-goal-area">Area <span className="blank-line-rule" /></span>
            <span className="blank-choices">
              {['One-time', 'Daily', 'Weekly', 'Monthly'].map((f) => (
                <span key={f}>
                  <Box /> {f}
                </span>
              ))}
            </span>
          </div>
        ))}

        <footer className="report-footer">
          <p className="report-footer-brand">Iron Forums · Connect » Sharpen » Grow</p>
          <p>Enter it in the app later under Snapshots → New Snapshot.</p>
        </footer>
      </section>
    </div>
  );
}

export default function BlankSnapshot() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const snapshotType = searchParams.get('type') || user?.snapshot_type || 'member';
  const [includeDefinitions, setIncludeDefinitions] = useState(true);
  const [loaded, setLoaded] = useState<{ type: string; categories: SnapshotCategory[] | null } | null>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    let cancelled = false;
    snapshotsAPI
      .getBlankForm(snapshotType)
      .then((data) => {
        if (!cancelled) setLoaded({ type: snapshotType, categories: data.categories });
      })
      .catch(() => {
        if (!cancelled) setLoaded({ type: snapshotType, categories: null });
      });
    return () => {
      cancelled = true;
    };
  }, [snapshotType]);

  const current = loaded?.type === snapshotType ? loaded : null;
  const categories = current?.categories ?? null;
  const failed = Boolean(current && !current.categories);

  // Phones show the sheet shrunk to fit, laid out exactly as it prints.
  useEffect(() => {
    const el = sheetRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      setZoom(Math.min(1, entry.contentRect.width / SHEET_WIDTH_PX));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [categories]);

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <div className="print-hidden space-y-4">
        <div className="flex items-center gap-3">
          <Link to="/snapshots" className="p-2 hover:bg-white rounded-lg transition" aria-label="Back to snapshots">
            <ArrowLeft size={24} className="text-navy" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-navy">Blank Snapshot</h1>
            <p className="text-gray-600">
              A paper version for guests and members who haven&rsquo;t done theirs in the app yet.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-sm flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="text-sm font-medium text-gray-700">
              Snapshot type
              <select
                value={snapshotType}
                onChange={(e) => setSearchParams({ type: e.target.value }, { replace: true })}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
              >
                {BLANK_SNAPSHOT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 sm:pb-2">
              <input
                type="checkbox"
                checked={includeDefinitions}
                onChange={(e) => setIncludeDefinitions(e.target.checked)}
              />
              Include &ldquo;Healthy looks like&rdquo; lines
            </label>
          </div>
          {isNativePlatform ? (
            <p className="text-sm text-gray-600 sm:max-w-xs">
              Printing isn&rsquo;t available in the phone app yet. Open app.ironforums.org/snapshots/blank on a
              computer to print.
            </p>
          ) : (
            <button
              type="button"
              onClick={() => window.print()}
              disabled={!categories}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy-dark transition disabled:opacity-50"
            >
              <Printer size={18} />
              Print / Save PDF
            </button>
          )}
        </div>
      </div>

      {failed && <p className="text-red-600 print-hidden">Could not load this Snapshot. Try again.</p>}
      {!categories && !failed && (
        <div className="flex items-center justify-center h-40 print-hidden">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-navy" />
        </div>
      )}
      {categories && (
        <div ref={sheetRef} className="bg-white rounded-2xl p-3 sm:p-6 shadow-sm blank-sheet">
          <div className="blank-fit" style={{ zoom }}>
            <BlankSnapshotForm
              snapshotType={snapshotType}
              categories={categories}
              includeDefinitions={includeDefinitions}
            />
          </div>
        </div>
      )}
    </div>
  );
}
